import { combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';

export interface DirtyComponent {
  isDirty$: Observable<boolean>;
  openConfirmation?: () => Observable<boolean>;
}
/** absolute tolerance for numeric comparisons (mph canonical) */
export const DIRTY_EPSILON = 0.1;

// Normalize for deep comparison
// ----- helpers (unchanged) -----
const normalize = (val: any): any => {
  if (val == null) return '';
  if (typeof val === 'string' && !isNaN(Number(val))) return Number(val);
  if (Array.isArray(val)) return val.map(normalize);
  if (typeof val === 'object') {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(val)) out[k] = normalize(v);
    return out;
  }
  return val;
};

const isEqual = (a: any, b: any, visited = new WeakMap()): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b || a == null || b == null) return false;
  if (typeof a !== 'object') return a === b;
  if (visited.has(a)) return visited.get(a) === b;
  visited.set(a, b);
  const ka = Object.keys(a),
    kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => isEqual(a[k], b[k], visited));
};

const getDiff = (a: any = {}, b: any = {}, visited = new WeakMap()): any => {
  if (a === b) return {};
  visited.set(a, b);
  const diff: any = {};
  for (const [k, newVal] of Object.entries(b)) {
    const oldVal = a[k];
    if (!isEqual(oldVal, newVal, visited)) {
      if (Array.isArray(newVal) && Array.isArray(oldVal)) {
        const added = newVal.filter((x) => !oldVal.includes(x));
        const removed = oldVal.filter((x) => !newVal.includes(x));
        if (added.length || removed.length) diff[k] = { added, removed };
      } else if (typeof newVal === 'object' && typeof oldVal === 'object') {
        const nested = getDiff(oldVal, newVal, visited);
        if (Object.keys(nested).length) diff[k] = nested;
      } else {
        diff[k] = newVal;
      }
    }
  }
  return diff;
};

/** Safe deep equal (no circular blowups) */
export function deepEqual(a: any, b: any, visited = new WeakMap()): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a == null || b == null) return a === b;
  if (typeof a !== 'object') return a === b;

  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }

  if (visited.has(a)) return visited.get(a) === b;
  visited.set(a, b);

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const k of keysA) {
    if (!deepEqual(a[k], b[k], visited)) return false;
  }
  return true;
}

/** Shallowish diff for debugging – returns { key: [serverVal, uiVal] } */
export function diffObject(a: any = {}, b: any = {}): Record<string, [any, any]> {
  const out: Record<string, [any, any]> = {};
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const k of keys) {
    const av = a?.[k];
    const bv = b?.[k];
    if (!deepEqual(av, bv)) out[k] = [av, bv];
  }
  return out;
}

/** Deep approximate equality with epsilon for numbers */
export function approxEqual(a: any, b: any, eps = DIRTY_EPSILON): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;

  const ta = typeof a;
  const tb = typeof b;

  if (ta === 'number' && tb === 'number') {
    return Math.abs(a - b) <= eps;
  }
  if (ta !== tb) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!approxEqual(a[i], b[i], eps)) return false;
    }
    return true;
  }

  if (ta === 'object') {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    for (const k of ka) {
      if (!approxEqual(a[k], b[k], eps)) return false;
    }
    return true;
  }

  return a === b;
}

/** Operator: dirtyCheck(baseline$)(value$) -> boolean dirty */
export function dirtyCheck<U>(baseline$: Observable<U>, eps = DIRTY_EPSILON) {
  return <T>(value$: Observable<T>): Observable<boolean> =>
    combineLatest([baseline$, value$]).pipe(
      // next microtask so template/form patches settle
      debounceTime(0),
      map(([a, b]) => !approxEqual(a, b, eps)),
      distinctUntilChanged(),
      startWith(false),
      shareReplay({ bufferSize: 1, refCount: true })
    );
}
// Utility: Array asymmetric difference
export function asymmetricArrayDiff<T extends String | Number | Boolean>(arr1: T[], arr2: T[]): T[] {
  arr1 = arr1 || [];
  arr2 = arr2 || [];

  const diff = arr1.filter((x) => !arr2.includes(x)).concat(arr2.filter((x) => !arr1.includes(x)));
  return diff;
}
